import React from 'react';
import {
  Typography,
  Box,
  CardContent,
  Chip,
  IconButton,
  Stack
} from '@mui/material';
import { GlassmorphicCard } from '../GlassmorphicCard/GlassmorphicCard';
import { ActionButton } from '../ActionButton/ActionButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import PlaceIcon from '@mui/icons-material/Place';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import './PlanCard.scss';

interface PlanCardProps {
  id: string;
  title: string;
  description?: string;
  location?: string;
  date?: string;
  tags?: string[];
  imageUrl?: string;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onBook?: (id: string) => void;
  onView?: (id: string) => void;
}

export const PlanCard: React.FC<PlanCardProps> = ({
  id,
  title,
  description,
  location,
  date,
  tags = [],
  imageUrl,
  onEdit,
  onDelete,
  onBook,
  onView
}) => {
  const handleEdit = () => onEdit && onEdit(id);
  const handleDelete = () => onDelete && onDelete(id);
  const handleBook = () => onBook && onBook(id);

  return (
    <GlassmorphicCard
      className="animate-hover plan-card-root"
      sx={{
        maxWidth: 345,
        width: '100%',
        minHeight: 300,
        borderRadius: '16px',
        display: 'flex',
        flexDirection: 'column',
        animation: 'fadeIn 0.6s ease-in-out forwards',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {imageUrl && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${imageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1
            }
          }}
        />
      )}
      <CardContent sx={{ flexGrow: 1, position: 'relative', zIndex: 2 }}>
        <Typography variant="h5" component="div" gutterBottom>
          {title}
        </Typography>

        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}

        <Box sx={{ mb: 2 }}>
          {location && (
            <Box className="info-item">
              <PlaceIcon fontSize="small" />
              <Typography variant="body2">{location}</Typography>
            </Box>
          )}

          {date && (
            <Box className="info-item">
              <CalendarTodayIcon fontSize="small" />
              <Typography variant="body2">{date}</Typography>
            </Box>
          )}
        </Box>

        {tags.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
            {tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                className="tag-chip"
              />
            ))}
          </Stack>
        )}
      </CardContent>

      <Box className="card-footer">
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {onEdit && (
            <IconButton
              onClick={handleEdit}
              size="small"
              aria-label="edit"
              className="edit-button-transparent"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {onDelete && (
            <IconButton
              onClick={handleDelete}
              size="small"
              aria-label="delete"
              className="delete-button-transparent"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {onBook && (
            <ActionButton
              onClick={handleBook}
              size="small"
              className="book-button-gradient"
            >
              Book
            </ActionButton>
          )}
        </Box>
      </Box>
    </GlassmorphicCard>
  );
};
